import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Minus, Plus, ShoppingBag, ArrowLeft, Check, ChevronLeft, ChevronRight } from "lucide-react";

import { useCart } from "@/store/cart";
import { useSettings } from "@/hooks/useSettings";
import { formatCurrency } from "@/lib/format";
import { toast } from "sonner";
import { ProductCard } from "@/components/store/ProductCard";
import { ProductGallery } from "@/components/store/ProductGallery";

const getProductGallery = (product: any) =>
  ([product?.image_url, ...(product?.images ?? [])].filter(Boolean) as string[]);

const isHexColor = (c: string) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c.trim());
const colorLabel = (c: string) => (isHexColor(c) ? c.toUpperCase() : c);

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState<any>(null);
  const [related, setRelated] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const addItem = useCart((s) => s.addItem);
  const setOpen = useCart((s) => s.setOpen);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setQty(1);
      const { data } = await supabase.from("products").select("*").eq("id", id).maybeSingle();
      setProduct(data);
      const firstColor = data?.colors?.[0] ?? null;
      setSelectedColor(firstColor);
      setSelectedSize(null);
      const initialImages = getProductGallery(data);
      setActiveImage(initialImages[0] ?? null);
      setLoading(false);

      if (data) {
        // Pull a wider candidate pool then score for relevance
        const { data: pool } = await supabase
          .from("products")
          .select("*")
          .neq("id", data.id)
          .limit(60);

        const candidates = pool ?? [];
        const basePrice = Number(data.sale_price ?? data.price ?? 0);
        const tokenize = (s?: string | null) =>
          (s ?? "")
            .toLowerCase()
            .split(/[^a-z0-9\u0980-\u09FF]+/i)
            .filter((t) => t.length > 2);
        const baseTokens = new Set([
          ...tokenize(data.name),
          ...tokenize(data.description),
          ...tokenize(data.category),
        ]);

        const scored = candidates.map((p: any) => {
          let score = 0;
          if (data.category_id && p.category_id === data.category_id) score += 50;
          if (data.category && p.category === data.category) score += 30;
          const pTokens = new Set([
            ...tokenize(p.name),
            ...tokenize(p.description),
            ...tokenize(p.category),
          ]);
          let overlap = 0;
          baseTokens.forEach((t) => pTokens.has(t) && overlap++);
          score += overlap * 4;
          const pPrice = Number(p.sale_price ?? p.price ?? 0);
          if (basePrice > 0 && pPrice > 0) {
            const diff = Math.abs(pPrice - basePrice) / basePrice;
            if (diff <= 0.25) score += 12;
            else if (diff <= 0.5) score += 6;
            else if (diff <= 1) score += 2;
          }
          if (p.stock > 0) score += 3;
          if (p.featured) score += 2;
          // Light randomization to avoid same 4 every load
          score += Math.random() * 5;
          return { p, score };
        });

        scored.sort((a, b) => b.score - a.score);
        const seen = new Set<string>();
        const list: any[] = [];
        for (const { p } of scored) {
          const key = p.id;
          const imgKey = p.image_url ?? p.name;
          if (seen.has(key) || seen.has(`img:${imgKey}`)) continue;
          seen.add(key);
          seen.add(`img:${imgKey}`);
          list.push(p);
          if (list.length >= 8) break;
        }
        setRelated(list);
      }
    };
    load();
    window.scrollTo({ top: 0 });
  }, [id]);

  if (loading) {
    return (
      <div className="container py-12 grid md:grid-cols-2 gap-12">
        <Skeleton className="aspect-square rounded-2xl" />
        <div className="space-y-4">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container py-24 text-center">
        <p className="text-muted-foreground mb-4">Product not found.</p>
        <Button variant="outline" asChild><Link to="/">Back to shop</Link></Button>
      </div>
    );
  }

  const effectivePrice = product.on_sale && product.sale_price ? Number(product.sale_price) : Number(product.price);
  const sizes: string[] = Array.isArray(product.sizes) ? product.sizes : [];
  const sizeStock: Record<string, number> = (product.size_stock && typeof product.size_stock === "object") ? product.size_stock : {};
  const hasSizeStock = Object.keys(sizeStock).length > 0;
  const sizeAvailable = (s: string) => !hasSizeStock || Number(sizeStock[s] ?? 0) > 0;
  const stockForSize = selectedSize && hasSizeStock ? Number(sizeStock[selectedSize] ?? 0) : product.stock;
  const outOfStock = sizes.length > 0
    ? (hasSizeStock ? sizes.every((s) => Number(sizeStock[s] ?? 0) <= 0) : product.stock <= 0)
    : product.stock <= 0;

  const handleAdd = () => {
    if (sizes.length > 0 && !selectedSize) {
      toast.error("Please select a size");
      return;
    }
    if ((product.colors?.length ?? 0) > 0 && !selectedColor) {
      toast.error("Please select a color");
      return;
    }
    const maxStock = sizes.length > 0 && hasSizeStock ? stockForSize : product.stock;
    addItem(
      {
        id: product.id,
        name: product.name,
        price: effectivePrice,
        image_url: product.image_url,
        stock: maxStock,
        size: selectedSize,
        color: selectedColor,
      },
      qty,
    );
    const parts = [
      selectedSize ? `Size ${selectedSize}` : null,
      selectedColor ? `Color ${colorLabel(selectedColor)}` : null,
    ].filter(Boolean);
    toast.success("Added to cart", {
      description: `${product.name}${parts.length ? ` • ${parts.join(" • ")}` : ""}`,
    });
    setOpen(true);
  };

  return (
    <div className="container py-8 md:py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to shop
      </Link>
      <div className="grid md:grid-cols-2 gap-8 md:gap-12">
        <ProductGallery product={product} selectedColor={selectedColor} activeImage={activeImage} setActiveImage={setActiveImage} />

        <div className="space-y-6">
          {product.category && (
            <p className="text-xs uppercase tracking-wider text-primary font-semibold">{product.category}</p>
          )}
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight">{product.name}</h1>
          <div className="flex items-baseline gap-3">
            <span className="font-display text-3xl font-bold">{formatCurrency(effectivePrice, symbol)}</span>
            {product.on_sale && product.sale_price && (
              <span className="text-lg text-muted-foreground line-through">{formatCurrency(Number(product.price), symbol)}</span>
            )}
          </div>
          <p className="text-foreground/70 leading-relaxed whitespace-pre-line">{product.description || "No description provided."}</p>

          <div className={`inline-flex items-center gap-2 text-sm rounded-full px-3 py-1.5 ${
            outOfStock ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"
          }`}>
            <Check className="h-4 w-4" />
            {outOfStock
              ? "Out of stock"
              : selectedSize && hasSizeStock
                ? `In stock — ${stockForSize} available (${selectedSize})`
                : `In stock — ${product.stock} available`}
          </div>

          {product.colors && product.colors.length > 0 && (
            <div className="space-y-2.5">
              <p className="text-sm font-medium">
                Color: <span className="text-muted-foreground">{selectedColor ? colorLabel(selectedColor) : "Select a color"}</span>
              </p>
              <div className="flex flex-wrap gap-2.5">
                {product.colors.map((c: string) => {
                  const isSelected = selectedColor === c;
                  const swatch = isHexColor(c) ? c : c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setSelectedColor(c)}
                      aria-pressed={isSelected}
                      title={colorLabel(c)}
                      className={`group relative inline-flex items-center gap-2 rounded-full border-2 pl-1.5 pr-3 py-1.5 text-sm font-medium transition-all duration-200 ${
                        isSelected
                          ? "border-primary bg-primary/5 shadow-soft scale-[1.03]"
                          : "border-foreground/10 hover:border-primary hover:scale-[1.03] bg-background"
                      }`}
                    >
                      <span
                        className="h-6 w-6 rounded-full ring-1 ring-foreground/10 shadow-sm"
                        style={{ background: swatch }}
                      />
                      <span className="capitalize">{colorLabel(c)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {sizes.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  Size: <span className="text-muted-foreground">{selectedSize ?? "Select a size"}</span>
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {sizes.map((s) => {
                  const isSelected = selectedSize === s;
                  const available = sizeAvailable(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => available && setSelectedSize(s)}
                      disabled={!available}
                      aria-pressed={isSelected}
                      className={`relative min-w-[3rem] rounded-xl border-2 px-4 py-2 text-sm font-medium transition-all duration-200 ${
                        !available
                          ? "border-foreground/10 text-muted-foreground/60 line-through cursor-not-allowed bg-muted/40"
                          : isSelected
                            ? "border-primary bg-primary text-primary-foreground shadow-soft scale-[1.03]"
                            : "border-foreground/10 hover:border-primary hover:scale-[1.03] bg-background"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}


          <div className="flex items-center gap-4 pt-2">
            <div className="flex items-center rounded-full border-2 border-foreground/10">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="grid h-12 w-12 place-items-center hover:bg-secondary rounded-l-full" disabled={qty <= 1}>
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-medium">{qty}</span>
              <button onClick={() => setQty(Math.min(stockForSize, qty + 1))} className="grid h-12 w-12 place-items-center hover:bg-secondary rounded-r-full" disabled={qty >= stockForSize}>
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button variant="hero" size="lg" onClick={handleAdd} disabled={outOfStock} className="flex-1">
              <ShoppingBag className="h-5 w-5" /> Add to cart
            </Button>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-20">
          <div className="mb-8">
            <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">
              You may also like
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold">Related products</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-6 lg:grid-cols-4 xl:grid-cols-5">
            {related.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
